// MTReturnsTab - configuration-driven Returns screen (MTWHT-RET-01 / PNW-1765).
// Replaces the MTReturnsStep placeholder at /mt/returns/returns.
//
// Filters (AC1): the set of filters is driven by the Return Config filters API
//   GET /v1/return-config/{CC}/filters. ONLY Entity Name and Entity TIN reuse the
//   shared MTKeyFilterPanel's native Entity + TAN/UEN dropdowns (MT entity/TAN
//   APIs). Every OTHER configured filter - Return Form, Financial Year, Month -
//   is rendered from the config in the panel's children slot as a dropdown,
//   ordered by displayOrder (options supplied by BE later). Year/Month selections
//   are read back from those config dropdowns for the Generate/List payloads.
//
// Grid columns (AC2) are driven by GET /v1/return-config/{CC}/headers.
//
// Action column (AC4): each row exposes View + Refresh. Clicking View opens the
//   generated return in a read-only Form / Return Details view (parity with the
//   India Returns flow): the key filters are FILLED with the row's scope and
//   DISABLED, and a "Go Back" affordance restores the list and re-enables the
//   filters. The Download action lives in the Form view header (same async
//   download API that used to sit on the row action icon).
//
// View data (PNW-1724) is fetched TAB-WISE: GET /v1/return-view/view/{tab}/{id}.
//   On View we load the first tab (FORM); switching to Return Details fetches
//   that tab's data on demand. Each tab's payload is cached in viewDataByTab so
//   re-selecting a tab does not refetch.
//
// Save (PNW-1724) is a single shared endpoint: PUT /v1/return-view/edit/{id},
//   body { rows: [{ uuid, values }] }. Both tabs use it: FORM saves its one
//   row (uuid = the return-generation id); Return Details bulk-saves every
//   edited line item uuid in one call.
import DownloadIcon from '@/assets/icons/DownloadIcon'
import EditIcon from '@/assets/icons/EditIcon'
import CloseIcon from '@/assets/icons/MTCancelIcon'
import SaveIcon from '@/assets/icons/MTSaveIcon'
import SearchIconBtn from '@/assets/icons/SearchIconBtn'
import { Search } from '@/components/common/index.js'
import { useSnackbar } from '@/contexts/SnackbarProvider'
import { MTCommonTable } from '@/multi-territory/mt-components/mt-shared/MTCommonTable'
import { MTCustomSelect as CustomSelectBase } from '@/multi-territory/mt-components/mt-shared/MTCustomSelect'
import type { MTKeyFilterViewData } from '@/multi-territory/mt-components/mt-shared/MTKeyFilterPanel'
import { MTKeyFilterPanel } from '@/multi-territory/mt-components/mt-shared/MTKeyFilterPanel'
import { MTReturnsActionsCell } from '@/multi-territory/mt-components/mt-shared/MTReturnsActionsCell'
import { useMTUnsavedChangesGuard } from '@/multi-territory/mt-hooks/useMTUnsavedChangesGuard'
import {
    editMTReturnView,
    enqueueMTReturnDownload,
    fetchMTReturnFilterData,
    fetchMTReturnGrid,
    fetchMTReturnView,
    fetchMTReturnViewConfig,
    fetchMTReturnViewMetadata,
    generateMTReturnView,
    refreshMTReturnView,
} from '@/multi-territory/mt-services/mt-returns'
import type {
    MTMetadataField,
    MTMetadataTableColumn,
} from '@/multi-territory/mt-types/mt-metadata'
import type {
    MTReturnEditRequest,
    MTReturnEditRow,
    MTReturnFilterConfig,
    MTReturnHeaderConfig,
    MTReturnMetaField,
    MTReturnMetaTab,
    MTReturnViewData,
} from '@/multi-territory/mt-types/mt-returns'
import { buildRegexErrorMessage } from '@/multi-territory/mt-utils/mt-regex-hint'
import {
    buildMTSearchField,
    formatMTSearchCriteria,
} from '@/multi-territory/mt-utils/mt-search-fields'
import {
    Box,
    IconButton,
    Skeleton,
    Tab,
    Tabs,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

const CustomSelect = CustomSelectBase as any

const ENTITY_KEYS = ['entity_name']
const TAN_KEYS = ['tax_identification_number']

// Tab keys from the metadata layout.
const FORM_TAB_KEY = 'FORM'
const RETURN_DETAILS_TAB_KEY = 'RETURN_DETAILS'

// Conditional-required rule (MT payee-master parity): "Other Foreign Payee Type"
// must not be blank when "Payee's Business Structure" = "OTHERS: PLEASE SPECIFY".
// Field keys are the normalized column labels (see mt-return-page.util.ts):
//   "PAYEE'S BUSINESS STRUCTURE" -> payees_business_structure
//   "OTHER FOREIGN PAYEE TYPE"   -> other_foreign_payee_type
const PAYEE_BUSINESS_STRUCTURE_KEY = 'payees_business_structure'
const OTHER_FOREIGN_PAYEE_TYPE_KEY = 'other_foreign_payee_type'
const BUSINESS_STRUCTURE_OTHERS_VALUE = 'OTHERS: PLEASE SPECIFY'

const norm = (v?: string | null) => (v ?? '').trim().toLowerCase()

/**
 * Conditional-required check (MT payee-master parity). When the given field is
 * the "Other Foreign Payee Type" column and it is blank, it is required if the
 * row's "Payee's Business Structure" resolves to "OTHERS: PLEASE SPECIFY".
 * Returns an error message string, or '' when the rule does not apply / passes.
 *
 * The rule fires when the metadata flags the field `conditionallyRequired`, or
 * (fallback) when the field_key matches the Other Foreign Payee Type column, so
 * it holds even if the BE metadata does not carry the flag.
 */
const validateConditionalRequired = (
  field: MTReturnMetaField,
  value: string,
  mergedRow: Record<string, any>
): string => {
  const isOtherForeignPayeeType =
    norm(field.field_key) === OTHER_FOREIGN_PAYEE_TYPE_KEY
  const flaggedConditional = Boolean(
    (field.validations as { conditionallyRequired?: boolean } | undefined)
      ?.conditionallyRequired
  )
  if (!isOtherForeignPayeeType && !flaggedConditional) return ''

  const trimmed = value.trim()
  if (trimmed && trimmed !== '-') return ''

  const businessStructure = String(
    mergedRow[PAYEE_BUSINESS_STRUCTURE_KEY] ??
      mergedRow[
        Object.keys(mergedRow).find(
          k => norm(k) === PAYEE_BUSINESS_STRUCTURE_KEY
        ) ?? ''
      ] ??
      ''
  )
    .trim()
    .toUpperCase()

  if (businessStructure === BUSINESS_STRUCTURE_OTHERS_VALUE) {
    return `${field.label} is required when Payee's Business Structure is "Others: Please Specify"`
  }
  return ''
}

/** Heuristic: does this regex look like an email pattern (contains an @)? */
const isEmailRegex = (re: string) => re.includes('@')

/** True when either the filterKey or sourceColumn matches one of the given keys. */
const matchesKey = (f: MTReturnFilterConfig, keys: string[]) =>
  keys.includes(norm(f.filterKey)) || keys.includes(norm(f.sourceColumn))

const isEntityFilter = (f: MTReturnFilterConfig) => matchesKey(f, ENTITY_KEYS)
const isTanFilter = (f: MTReturnFilterConfig) => matchesKey(f, TAN_KEYS)

const isNativeFilter = (f: MTReturnFilterConfig) =>
  isEntityFilter(f) || isTanFilter(f)

const isRenderableExtra = (f: MTReturnFilterConfig) =>
  !isNativeFilter(f) && !!f.filterKey

/** Stable data-accessor key for a grid column — always lowercase to match snake_case API row keys. */
const columnKey = (h: MTReturnHeaderConfig) =>
  (h.sourceColumn || h.columnHeader).toLowerCase().replace(/\s+/g, '_')

const isActionColumn = (h: MTReturnHeaderConfig) =>
  /icon/i.test(String(h.displayType)) ||
  /view\s*\+\s*refresh/i.test(String(h.displayType)) ||
  (/^action$/i.test(h.columnHeader.trim()) &&
    (!h.sourceColumn || h.sourceColumn === '-'))

const isStatusColumn = (h: MTReturnHeaderConfig) =>
  /status/i.test(String(h.displayType)) ||
  /status/i.test(String(h.sourceColumn || '')) ||
  /status/i.test(h.columnHeader)

const isMultiSelectType = (t?: string) =>
  t === 'MULTI_SELECT' || /multi/i.test(String(t))

// Identify which extra filter is Return Form, Year, Month by filterKey.
// Hoisted to module scope so they are stable across renders.
const isReturnFormFilter = (f: MTReturnFilterConfig) =>
  /return.form/i.test(f.filterKey)
const isYearFilter = (f: MTReturnFilterConfig) =>
  /^(year|financial_year)$/i.test(f.filterKey)
const isMonthFilter = (f: MTReturnFilterConfig) =>
  /^(month|period)$/i.test(f.filterKey)

// Hoisted so it is compiled once instead of on every resolveFieldValue call.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Section title style for the Returns Form tab ("1. Payer Information" etc).
const SECTION_TITLE_SX = {
  fontSize: '15px',
  fontWeight: 600,
  color: 'var(--forbrand-primary)',
  mb: 2,
} as const

// Two-column responsive grid for the form fields (label above input).
const FORM_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
  columnGap: '48px',
  rowGap: '20px',
  alignItems: 'start',
} as const

const MTReturnStatusBadge = React.memo(function MTReturnStatusBadge({
  status,
}: {
  status?: string
}) {
  const s = status?.toUpperCase() ?? ''
  const style: React.CSSProperties =
    s === 'COMPLETED'
      ? { backgroundColor: '#F0FDF3', color: '#197C37' }
      : s === 'FAILED' || s === 'DELETED'
        ? { backgroundColor: '#FEF3F2', color: '#E0301E' }
        : s === 'IN_PROGRESS' || s === 'IN PROGRESS'
          ? { backgroundColor: '#FFDFC4', color: '#D17300' }
          : { backgroundColor: '#E2E3E5', color: '#495057' }
  return (
    <span
      style={{
        ...style,
        padding: '2px 6px',
        borderRadius: 4,
        display: 'inline-block',
      }}
    >
      {status || 'N/A'}
    </span>
  )
})

/**
 * A single labelled field in the Returns Form view. Label sits above the input.
 * In edit mode the input is editable (white, bordered); otherwise it is a
 * disabled read-only field (India Returns Form parity).
 */
const MTReturnFormField = React.memo(function MTReturnFormField({
  label,
  value,
  required,
  editMode = false,
  fieldKey,
  onChange,
}: {
  label: string
  value: string
  required?: boolean
  editMode?: boolean
  fieldKey?: string
  onChange?: (key: string, value: string) => void
}) {
  return (
    <Box>
      <Typography
        component="label"
        sx={{
          display: 'block',
          fontSize: '14px',
          color: 'var(--forbrand-primary)',
          mb: 0.75,
          lineHeight: '140%',
        }}
      >
        {label}
        {required && (
          <span style={{ color: 'var(--forbrand-error, #d12121)' }}>*</span>
        )}
      </Typography>
      <input
        type="text"
        value={value ?? ''}
        disabled={!editMode}
        readOnly={!editMode}
        onChange={e => fieldKey && onChange?.(fieldKey, e.target.value)}
        style={{
          width: '100%',
          height: '44px',
          padding: '10px 12px',
          fontSize: '14px',
          border: '1px solid var(--border-strong, #8E95A2)',
          borderRadius: 0,
          background: editMode
            ? 'var(--bg-white, #ffffff)'
            : 'var(--bg-disabled, #dfe3e6)',
          color: 'var(--forbrand-primary, #111113)',
          boxSizing: 'border-box',
        }}
      />
    </Box>
  )
})

/**
 * Skeleton placeholder for the Returns FORM tab, shown only while the tab's data
 * is being fetched for the first time (before it lands in the per-tab cache).
 * Mirrors the real section/two-column field layout so the switch does not flash
 * empty inputs. Cached tabs never show this — re-selecting a tab is instant.
 */
const MTReturnFormSkeleton = React.memo(function MTReturnFormSkeleton({
  sections = 2,
  fieldsPerSection = 4,
}: {
  sections?: number
  fieldsPerSection?: number
}) {
  return (
    <Box
      sx={{ px: 3, py: 2.5 }}
      aria-busy="true"
      aria-label="Loading return form"
    >
      {Array.from({ length: sections }).map((_, si) => (
        <Box key={si} sx={si > 0 ? { mt: 4 } : undefined}>
          <Skeleton variant="text" width={220} height={24} sx={{ mb: 2 }} />
          <Box sx={FORM_GRID_SX}>
            {Array.from({ length: fieldsPerSection }).map((__, fi) => (
              <Box key={fi}>
                <Skeleton
                  variant="text"
                  width={140}
                  height={18}
                  sx={{ mb: 0.75 }}
                />
                <Skeleton
                  variant="rectangular"
                  height={44}
                  sx={{ borderRadius: 0 }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  )
})

const MTReturnsTab = () => {
  const { showSnackbar } = useSnackbar() as any
  const countryCode = useSelector(
    (state: { country: { countryCode: string } }) => state.country?.countryCode
  )
  const { selectedEntityUuid, selectedTanUuid, entityMetaByUuid } = useSelector(
    (state: any) => state.mtEntityTan
  )
  const selectedEntityMeta = selectedEntityUuid
    ? entityMetaByUuid?.[selectedEntityUuid]
    : null
  const tanApplicable = selectedEntityMeta?.tanApplicable ?? true
  const entityAndTanSelected =
    !!selectedEntityUuid && (tanApplicable ? !!selectedTanUuid : true)

  const [filters, setFilters] = useState<MTReturnFilterConfig[]>([])
  const [headers, setHeaders] = useState<MTReturnHeaderConfig[]>([])
  const [filterOptions, setFilterOptions] = useState<
    Record<string, { label: string; value: string }[]>
  >({})
  const [returnFormCode, setReturnFormCode] = useState<string>('')
  const [downloadingRowId, setDownloadingRowId] = useState<string | null>(null)
  const [extraValues, setExtraValues] = useState<
    Record<string, string | string[]>
  >({})
  // Labels shown in the Entity/TAN dropdowns while in view mode.
  const [viewModeLabels, setViewModeLabels] = useState<{
    entityName?: string
    tanOrUin?: string
  }>({})

  const [showTable, setShowTable] = useState(false)
  const [data, setData] = useState<Record<string, any>[]>([])
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [refreshingRowId, setRefreshingRowId] = useState<string | null>(null)
  const [lastFilterData, setLastFilterData] =
    useState<MTKeyFilterViewData | null>(null)

  // --- Form view (AC4 / PNW-1724) ---
  // The uuid of the row currently open in the read-only Form view (or null when
  // the status grid is shown). While set, the key filters are disabled.
  const [viewRowId, setViewRowId] = useState<string | null>(null)
  const [viewRow, setViewRow] = useState<Record<string, any> | null>(null)
  // Per-tab view data cache, keyed by tab_key (FORM / RETURN_DETAILS). Data is
  // fetched tab-wise via GET /return-view/view/{tab}/{id}.
  const [viewDataByTab, setViewDataByTab] = useState<
    Record<string, MTReturnViewData>
  >({})
  const [layoutTabs, setLayoutTabs] = useState<MTReturnMetaTab[]>([])
  // True while the active tab's data is being fetched.
  const [tabLoading, setTabLoading] = useState(false)
  // True while a save (PUT /return-view/edit/{id}) is in flight.
  const [saving, setSaving] = useState(false)
  // Per-field validation errors: { fieldKey: errorMessage }
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [viewingRowId, setViewingRowId] = useState<string | null>(null)
  const [editMode, setEditMode] = useState(false)
  const [formTab, setFormTab] = useState(0)
  // Pending edits while in edit mode. formEdits keyed by field key; detailEdits
  // is a bulk-edit map keyed by rowId -> { fieldKey: value } (MTCommonTable shape).
  const [formEdits, setFormEdits] = useState<Record<string, string>>({})
  const [detailEdits, setDetailEdits] = useState<
    Record<string, Record<string, string>>
  >({})
  const [detailPage, setDetailPage] = useState(1)
  const [detailPageSize, setDetailPageSize] = useState(100)
  // Server-side total for the RETURN_DETAILS tab (across all pages).
  const [detailTotalItems, setDetailTotalItems] = useState(0)

  const [totalItems, setTotalItems] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)

  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [searchCriteria, setSearchCriteria] = useState<any[]>([
    {
      id: 1,
      logicalOperator: 'Where',
      field: '',
      operator: 'EQUAL_TO',
      value: '',
    },
  ])
  const [searchList, setSearchList] = useState<any[]>([])

  const isFormView = !!viewRowId

  useEffect(() => {
    if (!countryCode) return
    fetchMTReturnViewConfig(countryCode).then(res => {
      if (!res.success || !res.data) return
      const cfg = res.data

      const activeFilters = (cfg.filters ?? [])
        .filter(f => norm(f.status) === 'active' || !f.status)
        .sort((a, b) => a.displayOrder - b.displayOrder)
      setFilters(activeFilters)

      const seed: Record<string, string | string[]> = {}
      activeFilters.filter(isRenderableExtra).forEach(f => {
        seed[extraKey(f)] = isMultiSelectType(f.filterType) ? [] : ''
      })
      setExtraValues(seed)

      const cols = (cfg.columns ?? [])
        .slice()
        .sort((a, b) => a.displayOrder - b.displayOrder)
      setHeaders(cols)

      if (cfg.returnFormCode) setReturnFormCode(cfg.returnFormCode)
    })

    fetchMTReturnFilterData(countryCode).then(res => {
      if (!res.success || !res.data) return
      const mapped: Record<string, { label: string; value: string }[]> = {}
      Object.entries(res.data).forEach(([filterKey, values]) => {
        mapped[filterKey] = (Array.isArray(values) ? values : []).map(v => ({
          label: String(v),
          value: String(v),
        }))
      })
      setFilterOptions(mapped)
    })
  }, [countryCode])

  const extraFilters = useMemo(
    () => filters.filter(isRenderableExtra),
    [filters]
  )

  const returnFormFilter = useMemo(
    () => extraFilters.find(isReturnFormFilter),
    [extraFilters]
  )
  const yearFilter = useMemo(
    () => extraFilters.find(isYearFilter),
    [extraFilters]
  )
  const monthFilter = useMemo(
    () => extraFilters.find(isMonthFilter),
    [extraFilters]
  )

  const returnFormValue = returnFormFilter
    ? String(extraValues[extraKey(returnFormFilter)] ?? '')
    : ''
  const yearValue = yearFilter
    ? String(extraValues[extraKey(yearFilter)] ?? '')
    : ''

  // Generate is disabled until all required extra filters are filled.
  const allExtraFilled = extraFilters.every(f => {
    const v = extraValues[extraKey(f)]
    return Array.isArray(v) ? v.length > 0 : !!v
  })

  const filterValuesByKey = useMemo(() => {
    const out: Record<string, string | string[]> = {}
    extraFilters.forEach(f => {
      out[f.filterKey || extraKey(f)] = extraValues[extraKey(f)] ?? ''
    })
    return out
  }, [extraFilters, extraValues])

  const currentForm =
    String(filterValuesByKey['return_form_code'] ?? '') ||
    returnFormCode ||
    'IR37'

  const displayColumns = useMemo<MTMetadataTableColumn[]>(() => {
    const renderAction = (_v: any, row: any) => {
      const rowId = row?.uuid || row?.id || ''
      const isThisViewing = viewingRowId === rowId
      const isThisRefreshing = refreshingRowId === rowId
      const anyBusy = !!viewingRowId || !!refreshingRowId
      return (
        <MTReturnsActionsCell
          rowId={rowId}
          onView={rid => handleView(rid, row)}
          onRefresh={handleRefresh}
          refreshing={isThisRefreshing}
          viewing={isThisViewing}
          viewDisabled={!rowId || (anyBusy && !isThisViewing)}
          refreshDisabled={anyBusy && !isThisRefreshing}
        />
      )
    }

    const cols: MTMetadataTableColumn[] = headers.map(h => ({
      id: isActionColumn(h) ? '_returns_action' : columnKey(h),
      label: h.columnHeader,
      type: 'text',
      sortable: !isActionColumn(h),
      editable: false,
      ...(isActionColumn(h) ? { width: '120px' } : {}),
      minWidth: '120px',
      render: isActionColumn(h)
        ? renderAction
        : isStatusColumn(h)
          ? (value: any) => <MTReturnStatusBadge status={value} />
          : undefined,
    }))

    if (!cols.length) return []
    const hasActionColumn = headers.some(isActionColumn)
    if (hasActionColumn) return cols
    return [
      ...cols,
      {
        id: '_returns_action',
        label: 'Action',
        type: 'text',
        sortable: false,
        editable: false,
        width: '120px',
        minWidth: '120px',
        render: renderAction,
      },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers, refreshingRowId, viewingRowId])

  const searchFields = useMemo(
    () =>
      // buildMTSearchField formats the label like the table header (sentence
      // case, acronyms uppercase) AND, via the config displayType, renders date
      // columns (e.g. "Update Date", DATETIME) as a date picker instead of a
      // plain text field.
      // Exclude the Action column — it holds per-row icon buttons (View +
      // Refresh), not searchable data, so it must not appear in the search
      // field dropdown.
      headers
        .filter(h => !isActionColumn(h))
        .map(h =>
          buildMTSearchField({
            id: columnKey(h),
            label: h.columnHeader,
            displayType: h.displayType,
          })
        ) as any[],
    [headers]
  )

  const handleExtraChange = (key: string, value: string | string[]) => {
    setExtraValues(prev => ({ ...prev, [key]: value }))
  }

  // Auto-load grid on mount/reload with empty filters — show all records.
  const autoLoadedRef = useRef(false)
  useEffect(() => {
    if (autoLoadedRef.current || !countryCode || !headers.length) return
    autoLoadedRef.current = true
    setShowTable(true)
    setLoading(true)
    fetchMTReturnGrid(countryCode, 1, itemsPerPage, {
      filters: {},
      searchCOList: [],
    })
      .then(res => {
        if (res.success) {
          setData((res.data?.rows as Record<string, any>[]) ?? [])
          setTotalItems(res.data?.totalRecords ?? 0)
        } else {
          showSnackbar(res.error || 'Failed to load returns', 'error')
        }
      })
      .catch(() => showSnackbar('Failed to load returns', 'error'))
      .finally(() => setLoading(false))
  }, [countryCode, headers.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadReturns = useCallback(
    async ({
      page = 1,
      pageSize = itemsPerPage,
      search = searchList,
    }: {
      page?: number
      pageSize?: number
      search?: any[]
    } = {}) => {
      // The Returns grid is not entity-scoped — it auto-loads the full list on
      // mount (empty filters) and the same grid API backs Advanced Search and
      // pagination. Only country context is required; requiring a selected
      // entity here made Advanced Search / pagination silently no-op before an
      // entity was picked.
      if (!countryCode) return
      setLoading(true)
      try {
        // NOTE: BE currently ignores structured filters for the grid; scope is
        // applied server-side from the session. Only search criteria are sent.
        const res = await fetchMTReturnGrid(countryCode, page, pageSize, {
          filters: {},
          searchCOList: search,
        })
        if (res.success) {
          const body = res.data
          setData((body?.rows as Record<string, any>[]) ?? [])
          setTotalItems(body?.totalRecords ?? 0)
        } else {
          showSnackbar(res.error || 'Failed to load returns', 'error')
        }
      } catch {
        showSnackbar('Failed to load returns', 'error')
      } finally {
        setLoading(false)
      }
    },
    [countryCode, filterValuesByKey, itemsPerPage, searchList, showSnackbar]
  )

  const handleGenerate = useCallback(
    async (fd: MTKeyFilterViewData) => {
      if (!countryCode) return
      const selectedTaxId = fd.tanApplicable
        ? fd.tanNumber
        : fd.uniqueIdentificationNumber
      const form =
        String(filterValuesByKey['return_form_code'] ?? '') ||
        returnFormCode ||
        'IR37'
      const body = {
        entityTaxId: selectedTaxId || '5511885511A',
        entityName: fd.entityName || '04_VTD',
        tanMasterUuid: fd.tanMasterUuid ?? undefined,
        financialYear:
          (filterValuesByKey['year'] as string | undefined) || '2026',
        period: (filterValuesByKey['month'] as string | undefined) || '5',
      }

      setLastFilterData(fd)
      setShowTable(true)
      setCurrentPage(1)
      setGenerating(true)
      try {
        const res = await generateMTReturnView(countryCode, form, body)
        if (res.success) {
          if (res.data?.status === 'SUCCESS') {
            // Prefer the server-provided message (e.g. "Return Generated").
            // Generation is async (trackingStatus IN_PROGRESS) so there is no
            // record count to surface here.
            showSnackbar(
              res.data.message || 'Return generated successfully',
              'success'
            )
          } else {
            showSnackbar(res.data?.error || 'Generation failed', 'error')
          }
        } else {
          showSnackbar(res.error || 'Generation failed', 'error')
        }
      } catch {
        showSnackbar('Generation failed', 'error')
      } finally {
        setGenerating(false)
      }
      await loadReturns({ page: 1 })
    },
    [countryCode, filterValuesByKey, returnFormCode, loadReturns, showSnackbar]
  )

  // Fetch a single tab's view data (GET /return-view/view/{tab}/{id}) and cache
  // it by tab_key. Returns the fetched payload (or null on failure) so callers
  // can chain (e.g. fill filters from the FORM tab response).
  const loadTabData = useCallback(
    async (
      rowId: string,
      tabKey: string,
      pagination?: { page?: number; size?: number }
    ): Promise<MTReturnViewData | null> => {
      if (!rowId || !countryCode || !tabKey) return null
      setTabLoading(true)
      try {
        // RETURN_DETAILS is paginated server-side (India parity); FORM is not.
        const res = await fetchMTReturnView(
          rowId,
          countryCode,
          tabKey,
          tabKey === RETURN_DETAILS_TAB_KEY ? pagination : undefined
        )
        if (!res.success) {
          showSnackbar(res.error || 'Failed to load return', 'error')
          return null
        }
        const payload = res.data
        setViewDataByTab(prev => ({ ...prev, [tabKey]: payload }))
        if (tabKey === RETURN_DETAILS_TAB_KEY) {
          setDetailTotalItems(
            payload?.totalRecords ??
              payload?.totalElements ??
              payload?.rows?.length ??
              0
          )
        }
        return payload
      } catch {
        showSnackbar('Failed to load return', 'error')
        return null
      } finally {
        setTabLoading(false)
      }
    },
    [countryCode, showSnackbar]
  )

  // Load a specific page of the RETURN_DETAILS tab from the server (India parity:
  // each page change is a fresh GET /return-view/view/RETURN_DETAILS/{id}?page=&size=).
  const loadDetailPage = useCallback(
    async (page: number, size: number) => {
      if (!viewRowId) return
      setDetailPage(page)
      setDetailPageSize(size)
      await loadTabData(viewRowId, RETURN_DETAILS_TAB_KEY, { page, size })
    },
    [viewRowId, loadTabData]
  )

  // View (AC4): fetch metadata (layout) first to know the tabs, then fetch the
  // FIRST tab's data (FORM). Return Details is fetched on demand when the user
  // switches tabs (handleFormTabChange).
  const handleView = useCallback(
    async (rowId: string, row: Record<string, any>) => {
      if (!rowId || !countryCode) return
      setViewingRowId(rowId)
      try {
        const formType = row.return_form || row.return_form_code || currentForm
        const metaRes = await fetchMTReturnViewMetadata(countryCode, formType)

        const tabs =
          metaRes.success && Array.isArray(metaRes.data?.tabs)
            ? metaRes.data.tabs
            : []
        // Prefer the FORM tab as the initial tab; otherwise use the first tab.
        const firstTabIndex = Math.max(
          0,
          tabs.findIndex(t => t.tab_key === FORM_TAB_KEY)
        )
        const firstTabKey = tabs[firstTabIndex]?.tab_key || FORM_TAB_KEY

        // Reset the per-tab cache + detail paging for the newly opened row.
        setViewDataByTab({})
        setDetailPage(1)
        setDetailTotalItems(0)
        // Load tab 1 — server-side page 1 when RETURN_DETAILS, else plain.
        const firstTabData = await fetchMTReturnView(
          rowId,
          countryCode,
          firstTabKey,
          firstTabKey === RETURN_DETAILS_TAB_KEY
            ? { page: 1, size: detailPageSize }
            : undefined
        )
        if (!firstTabData.success) {
          showSnackbar(firstTabData.error || 'Failed to load return', 'error')
          return
        }
        setViewDataByTab({ [firstTabKey]: firstTabData.data })
        if (firstTabKey === RETURN_DETAILS_TAB_KEY) {
          setDetailTotalItems(
            firstTabData.data?.totalRecords ??
              firstTabData.data?.totalElements ??
              firstTabData.data?.rows?.length ??
              0
          )
        }
        setLayoutTabs(tabs)

        // Fill extra filters (Return Form, Year, Month) from the row.
        setExtraValues(prev => {
          const next = { ...prev }
          extraFilters.forEach(f => {
            const key = extraKey(f)
            const fk = norm(f.filterKey)
            if (fk === 'return_form' || fk === 'return_form_code') {
              const v = row.return_form || row.return_form_code || ''
              if (v) next[key] = v
            } else if (fk === 'year' || fk === 'financial_year') {
              const v = row.year || row.financial_year || ''
              if (v) next[key] = v
            } else if (fk === 'month' || fk === 'period') {
              const v = row.month || row.period || ''
              if (v) next[key] = v
            }
          })
          return next
        })

        setViewModeLabels({
          entityName: row.entity_name || '',
          tanOrUin:
            row.entity_tax_identification_number || row.tan_number || '',
        })
        setViewRow(row)
        setViewRowId(rowId)
        setFormTab(firstTabIndex)
        setEditMode(false)
        setFormErrors({})
      } catch {
        showSnackbar('Failed to load return', 'error')
      } finally {
        setViewingRowId(null)
      }
    },
    [countryCode, currentForm, extraFilters, showSnackbar, detailPageSize]
  )

  const handleGoBack = useCallback(() => {
    setExtraValues(prev => {
      const reset: Record<string, string | string[]> = {}
      Object.keys(prev).forEach(k => {
        reset[k] = Array.isArray(prev[k]) ? [] : ''
      })
      return reset
    })
    setViewModeLabels({})
    setLayoutTabs([])
    setViewRowId(null)
    setViewRow(null)
    setViewDataByTab({})
    setEditMode(false)
    setFormTab(0)
    setFormEdits({})
    setDetailEdits({})
    setFormErrors({})
    setDetailPage(1)
    setDetailTotalItems(0)
  }, [])

  // Edit-mode toggles (India Returns parity): Edit enters edit mode; Save commits
  // via PUT /return-view/edit/{id} and exits; Cancel discards pending edits.
  const handleEnterEdit = useCallback(() => {
    setFormEdits({})
    setDetailEdits({})
    setEditMode(true)
  }, [])

  const handleCancelEdit = useCallback(() => {
    setFormEdits({})
    setDetailEdits({})
    setEditMode(false)
  }, [])

  // Unsaved-changes guard: while in edit mode, Go Back, Form / Return Details
  // tab switch, detail pagination and navigating away ask for confirmation.
  const { guardUnsavedChanges, unsavedChangesDialog } =
    useMTUnsavedChangesGuard(editMode, handleCancelEdit)

  const handleFormFieldChange = useCallback((key: string, value: string) => {
    setFormEdits(prev => ({ ...prev, [key]: value }))
  }, [])

  // Block tab switching while editing (India Returns parity) - the user must
  // save or cancel their changes before moving between Form / Return Details.
  // On switch, fetch the target tab's data if it is not already cached.
  const handleFormTabChange = useCallback(
    (next: number) => {
      setFormTab(next)
      const targetTab = layoutTabs[next]
      if (targetTab && viewRowId && !viewDataByTab[targetTab.tab_key]) {
        if (targetTab.tab_key === RETURN_DETAILS_TAB_KEY) {
          // Return Details paginates server-side; load page 1 with the current size.
          setDetailPage(1)
          loadTabData(viewRowId, targetTab.tab_key, {
            page: 1,
            size: detailPageSize,
          })
        } else {
          loadTabData(viewRowId, targetTab.tab_key)
        }
      }
    },
    [layoutTabs, viewRowId, viewDataByTab, loadTabData, detailPageSize]
  )

  const handleDetailFieldChange = useCallback(
    (rowId: string, fieldKey: string, value: string) => {
      setDetailEdits(prev => ({
        ...prev,
        [rowId]: { ...(prev[rowId] || {}), [fieldKey]: value },
      }))
    },
    []
  )

  // Download - moved from the row action icon into the Form view header. Same
  // async id-based flow as before (enqueue + surface the allocated download id).
  const handleDownload = useCallback(async () => {
    const rowId = viewRowId
    if (!rowId || !countryCode) return
    setDownloadingRowId(rowId)
    showSnackbar('Request Initiated', 'info')
    try {
      const enq = await enqueueMTReturnDownload(countryCode, currentForm, rowId)
      if (enq.success) {
        const downloadId = Number(enq.data)
        showSnackbar(
          `Download request initiated (ID: ${downloadId})`,
          'success'
        )
      } else {
        showSnackbar(enq.error || 'Unable to start download', 'error')
      }
    } catch {
      showSnackbar('Download failed', 'error')
    } finally {
      setDownloadingRowId(null)
    }
  }, [countryCode, currentForm, viewRowId, showSnackbar])

  const handleRefresh = useCallback(
    async (rowId: string) => {
      if (!rowId || !countryCode) return
      setRefreshingRowId(rowId)
      try {
        const res = await refreshMTReturnView(countryCode, rowId)
        if (res.success && res.data?.status !== 'FAILED') {
          showSnackbar('Return refreshed', 'success')
          // Reload grid — bypass lastFilterData guard for auto-loaded grid.
          setLoading(true)
          fetchMTReturnGrid(countryCode, currentPage, itemsPerPage, {
            filters: {},
            searchCOList: searchList,
          })
            .then(r => {
              if (r.success) {
                setData((r.data?.rows as Record<string, any>[]) ?? [])
                setTotalItems(r.data?.totalRecords ?? 0)
              }
            })
            .finally(() => setLoading(false))
        } else {
          showSnackbar(
            res.success
              ? res.data?.error || 'Refresh failed'
              : res.error || 'Refresh failed',
            'error'
          )
        }
      } catch {
        showSnackbar('Refresh failed', 'error')
      } finally {
        setRefreshingRowId(null)
      }
    },
    [countryCode, currentPage, itemsPerPage, searchList, showSnackbar]
  )

  const handlePageChange = async (page: number) => {
    setCurrentPage(page)
    await loadReturns({ page })
  }
  const handleItemsPerPageChange = async (size: number) => {
    setItemsPerPage(size)
    setCurrentPage(1)
    await loadReturns({ page: 1, pageSize: size })
  }

  const handleSearch = (e: React.MouseEvent<HTMLElement>) => {
    setSearchOpen(v => !v)
    setAnchorEl(e.currentTarget)
  }
  const handleCloseSearch = () => {
    setSearchOpen(false)
    setAnchorEl(null)
  }
  const handleApplySearch = async (criteria: any[]) => {
    const apiCriteria = formatMTSearchCriteria(criteria)
    const mapped = apiCriteria.map((item: any, idx: number) => ({
      ...item,
      field: item.fieldName || item.field || '',
      id: item.id || idx + 1,
      logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
    }))
    setSearchCriteria(mapped)
    setSearchList(apiCriteria)
    setCurrentPage(1)
    await loadReturns({ page: 1, search: apiCriteria })
    setSearchOpen(false)
  }
  const handleResetSearch = async () => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: '',
        operator: 'EQUAL_TO',
        value: '',
      },
    ])
    setSearchList([])
    setCurrentPage(1)
    await loadReturns({ page: 1, search: [] })
    setSearchOpen(false)
  }

  // Tabs from metadata: find FORM and RETURN_DETAILS tabs.
  const formTabMeta = useMemo(
    () => layoutTabs.find(t => t.tab_key === FORM_TAB_KEY),
    [layoutTabs]
  )
  const detailTabMeta = useMemo(
    () => layoutTabs.find(t => t.tab_key === RETURN_DETAILS_TAB_KEY),
    [layoutTabs]
  )

  // Tab-wise data: FORM tab payload and RETURN_DETAILS tab payload come from the
  // per-tab cache (each fetched via GET /return-view/view/{tab}/{id}).
  const formViewData = useMemo(
    () => viewDataByTab[FORM_TAB_KEY] ?? null,
    [viewDataByTab]
  )
  const detailViewData = useMemo(
    () => viewDataByTab[RETURN_DETAILS_TAB_KEY] ?? null,
    [viewDataByTab]
  )

  const detailLayoutFields = useMemo<MTReturnMetaField[]>(() => {
    if (!detailTabMeta) return []
    return detailTabMeta.sections
      .flatMap(s => s.fields)
      .filter(f => f.visible)
      .sort((a, b) => a.order - b.order)
  }, [detailTabMeta])

  // Data source for resolving FORM field values.
  // Explicit aliases bridge key mismatches between grid row, viewData camelCase,
  // and metadata field_keys. Aliases are computed from originals BEFORE spreading
  // so the spread cannot overwrite them with undefined.
  const formSource: Record<string, any> = useMemo(() => {
    const row: Record<string, any> = viewRow ?? {}
    const data: Record<string, any> = formViewData ?? {}
    // The FORM view response nests the filled field values under `values`
    // (keyed by field_key, e.g. entity_name / entity_tax_id / financial_year /
    // period / final_return_tds_amount). These take precedence over the row.
    const values: Record<string, any> =
      (data.values as Record<string, any>) ?? {}
    const aliases: Record<string, any> = {}
    // entity_tax_id: grid row uses entity_tax_identification_number
    const taxId =
      values.entity_tax_id ||
      row.entity_tax_id ||
      row.entity_tax_identification_number ||
      data.entity_tax_id ||
      ''
    if (taxId) aliases.entity_tax_id = taxId
    // financial_year: values/viewData camelCase financialYear; grid row uses year
    const fy =
      values.financial_year ||
      (data as any).financialYear ||
      data.financial_year ||
      row.financial_year ||
      row.year ||
      ''
    if (fy) aliases.financial_year = fy
    // period: values/viewData camelCase period; grid row uses month
    const period =
      values.period || (data as any).period || row.period || row.month || ''
    if (period) aliases.period = period
    // return_form_code: viewData camelCase returnFormCode; grid row uses return_form
    const rfc =
      (data as any).returnFormCode ||
      data.return_form_code ||
      row.return_form_code ||
      row.return_form ||
      ''
    if (rfc) aliases.return_form_code = rfc
    return { ...row, ...data, ...values, ...aliases }
  }, [viewRow, formViewData])

  // Normalized (lowercased-key) view of formSource so field lookups are O(1)
  // instead of scanning every key for each rendered field.
  const normalizedSource = useMemo(() => {
    const m: Record<string, any> = {}
    for (const k of Object.keys(formSource)) m[norm(k)] = formSource[k]
    return m
  }, [formSource])

  // Resolve a field value by field_key. Guards against UUID values.
  const resolveFieldValue = useCallback(
    (fieldKey: string): string => {
      const val = normalizedSource[norm(fieldKey)]
      if (val == null || val === '') return ''
      const s = String(val)
      return UUID_RE.test(s) ? '' : s
    },
    [normalizedSource]
  )

  // Validate a single field value against its metadata validations.
  const validateField = (field: MTReturnMetaField, value: string): string => {
    const v = value.trim()
    const rules = field.validations
    // Required — prefer the server-provided message.
    if (rules?.required && !v)
      return rules.required_message || `${field.label} is required`
    // Nothing else to check when the (optional) field is empty.
    if (!v) return ''
    // Max length.
    if (rules?.maxLength && v.length > rules.maxLength)
      return `${field.label} must be at most ${rules.maxLength} characters`
    // Regex — the metadata sometimes sends the literal "DD/MM/YYYY" as a date
    // FORMAT hint rather than a real pattern; validate those as a date instead.
    if (rules?.regex) {
      if (rules.regex === 'DD/MM/YYYY') {
        if (!/^\d{2}\/\d{2}\/\d{4}$/.test(v))
          return `${field.label} must be a valid date in DD/MM/YYYY format (e.g. 31/12/2026)`
      } else {
        try {
          if (!new RegExp(rules.regex).test(v)) {
            // Prefer BE message, else a friendly per-pattern hint, else derive
            // a human-readable description of the regex constraint.
            if (rules.regex_message) return String(rules.regex_message)
            if (isEmailRegex(rules.regex))
              return `${field.label} must be a valid email address (e.g. name@example.com)`
            // The hint util recognises [0-9]; metadata amount patterns use \d,
            // so normalise \d -> [0-9] to get a friendly decimal description.
            return buildRegexErrorMessage(
              field.label,
              rules.regex.replace(/\\d/g, '[0-9]'),
              rules.regex_message as string | undefined
            )
          }
        } catch {
          /* malformed regex — skip */
        }
      }
    }
    return ''
  }

  // Validate all editable FORM fields; returns true if valid.
  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {}
    formTabMeta?.sections.forEach(s =>
      s.fields
        .filter(f => f.visible && f.editable)
        .forEach(f => {
          const val = formEdits[f.field_key] ?? resolveFieldValue(f.field_key)
          const err = validateField(f, val)
          if (err) errors[f.field_key] = err
        })
    )
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }, [formTabMeta, formEdits, resolveFieldValue])

  // Return Details tab: line items from the RETURN_DETAILS tab view response.
  const rawDetailRows: Record<string, any>[] = useMemo(() => {
    if (Array.isArray(detailViewData?.rows))
      return detailViewData!.rows as Record<string, any>[]
    return []
  }, [detailViewData])

  const detailRows: Record<string, any>[] = useMemo(() => {
    if (!rawDetailRows.length) return []
    return rawDetailRows.map((row, i) => {
      const out: Record<string, any> = {
        uuid: row.uuid || row.id || `detail_${i}`,
      }
      detailLayoutFields.forEach(f => {
        const hit = Object.keys(row).find(k => norm(k) === norm(f.field_key))
        out[f.field_key] = hit != null ? row[hit] : ''
      })
      const sNoField = detailLayoutFields.find(f => f.field_key === 's/no')
      if (sNoField && !out['s/no']) out['s/no'] = String(i + 1)
      return out
    })
  }, [rawDetailRows, detailLayoutFields])

  // Validate ONLY the RETURN_DETAILS rows the user actually edited (MT payee-
  // master parity) — untouched rows never light up red. Returns per-cell errors
  // (for the inline red highlight) plus ordered "Row N: message" strings for the
  // snackbar. Each edited row is validated on its merged (original + edits) values.
  const validateDetails = useCallback((): {
    valid: boolean
    messages: string[]
  } => {
    const messages: string[] = []

    Object.entries(detailEdits)
      .filter(([, values]) => values && Object.keys(values).length > 0)
      .forEach(([rowId]) => {
        const row = detailRows.find(r => String(r.uuid) === String(rowId))
        if (!row) return
        const rowIndex = detailRows.findIndex(
          r => String(r.uuid) === String(rowId)
        )
        // Merge original row values with the user's pending edits so sibling-
        // dependent rules (e.g. Other Foreign Payee Type vs Payee's Business
        // Structure) evaluate against the row's effective state.
        const mergedRow = { ...row, ...(detailEdits[rowId] || {}) }
        detailLayoutFields
          .filter(f => f.editable)
          .forEach(f => {
            const val = String(
              detailEdits[rowId]?.[f.field_key] ?? row[f.field_key] ?? ''
            )
            const err = validateField(f, val)
            if (err) {
              messages.push(`Row ${rowIndex + 1}: ${err}`)
              return
            }
            // Conditional required (MT payee-master parity): Other Foreign
            // Payee Type must not be blank when Payee's Business Structure is
            // "OTHERS: PLEASE SPECIFY".
            const condErr = validateConditionalRequired(f, val, mergedRow)
            if (condErr) messages.push(`Row ${rowIndex + 1}: ${condErr}`)
          })
      })

    return { valid: messages.length === 0, messages }
  }, [detailLayoutFields, detailRows, detailEdits])

  // Save (PNW-1724): PUT /return-view/edit/{id} — { rows: [{ uuid, values }] }.
  // FORM tab saves its single row (uuid = the open return's id, viewRowId).
  // RETURN_DETAILS bulk-saves every line item that has pending edits in one call.
  const handleSaveEdit = useCallback(async () => {
    if (!viewRowId || !countryCode) return
    const activeTab = layoutTabs[formTab]
    const isTableTab = activeTab?.tab_key !== FORM_TAB_KEY

    if (isTableTab) {
      // Validate only edited rows; surface each row's error as its own snackbar
      // (staggered) plus inline red cells (MT payee-master parity).
      const { valid, messages } = validateDetails()
      if (!valid) {
        messages.forEach((msg, i) =>
          setTimeout(() => showSnackbar(msg, 'error'), i * 700)
        )
        return
      }
    } else if (!validateForm()) {
      showSnackbar('Please fix validation errors before saving', 'error')
      return
    }

    // Body shape differs per tab:
    // - RETURN_DETAILS: bulk edit → { rows: [{ uuid, values }] } (per-item uuids)
    // - FORM: single row → { values: {...} } (uuid is on the URL path)
    let body: MTReturnEditRequest
    if (isTableTab) {
      const rows: MTReturnEditRow[] = Object.entries(detailEdits)
        .filter(([, values]) => values && Object.keys(values).length > 0)
        .map(([rowUuid, values]) => ({ uuid: rowUuid, values }))
      if (!rows.length) {
        showSnackbar('No changes to save', 'info')
        setEditMode(false)
        return
      }
      body = { rows }
    } else {
      if (!Object.keys(formEdits).length) {
        showSnackbar('No changes to save', 'info')
        setEditMode(false)
        return
      }
      body = { values: formEdits }
    }

    const tabKey = isTableTab ? RETURN_DETAILS_TAB_KEY : FORM_TAB_KEY

    setSaving(true)
    try {
      const res = await editMTReturnView(countryCode, tabKey, viewRowId, body)
      if (!res.success) {
        showSnackbar(res.error || 'Failed to save changes', 'error')
        return
      }
      // The edit API returns HTTP 200 even on a business failure
      // (e.g. { success: true, data: { status: 'FAILED', error: '...' } }),
      // so also inspect the payload status when the transport call succeeded.
      const editData = res.data as
        | { status?: string; error?: string; message?: string }
        | string
        | null
      if (
        editData &&
        typeof editData === 'object' &&
        editData.status === 'FAILED'
      ) {
        showSnackbar(editData.error || 'Failed to save changes', 'error')
        return
      }

      // Show the server's success message (e.g. "Updated successfully") for
      // both tabs; fall back to a generic message if none is returned.
      const successMsg =
        editData && typeof editData === 'object' && editData.message
          ? editData.message
          : 'Changes saved'
      showSnackbar(successMsg, 'success')
      setEditMode(false)

      // Re-fetch the saved tab from the server so the grid/form shows the
      // authoritative persisted values (not just the optimistic edits).
      if (isTableTab) {
        setDetailEdits({})
        await loadTabData(viewRowId, RETURN_DETAILS_TAB_KEY, {
          page: detailPage,
          size: detailPageSize,
        })
      } else {
        setFormEdits({})
        await loadTabData(viewRowId, FORM_TAB_KEY)
      }
    } catch {
      showSnackbar('Failed to save changes', 'error')
    } finally {
      setSaving(false)
    }
  }, [
    viewRowId,
    countryCode,
    layoutTabs,
    formTab,
    validateForm,
    validateDetails,
    detailEdits,
    formEdits,
    detailPage,
    detailPageSize,
    loadTabData,
    showSnackbar,
  ])

  // Resolve the control type for a RETURN_DETAILS field. Fields that carry
  // `options` render as a dropdown/select cell; otherwise fall back to the
  // declared type (number/text).
  const detailFieldType = (
    f: MTReturnMetaField
  ): MTMetadataTableColumn['type'] => {
    if (Array.isArray(f.options) && f.options.length) return 'dropdown'
    // Date fields come as type "text" with a DD/MM/YYYY regex hint — render a
    // date picker for those.
    if (f.type === 'date' || f.validations?.regex === 'DD/MM/YYYY')
      return 'date'
    if (f.type === 'number') return 'number'
    return 'text'
  }

  const detailColumns = useMemo<MTMetadataTableColumn[]>(() => {
    if (detailLayoutFields.length) {
      return detailLayoutFields.map(f => ({
        id: f.field_key,
        label: f.label,
        type: detailFieldType(f),
        sortable: false,
        editable: f.editable,
        minWidth: '140px',
        options: f.options,
      }))
    }
    const first = rawDetailRows[0]
    if (!first) return []
    return Object.keys(first).map(k => ({
      id: k,
      label: k,
      type: 'text',
      sortable: false,
      editable: true,
      minWidth: '140px',
    }))
  }, [detailLayoutFields, rawDetailRows])

  // Metadata fields for MTCommonTable so dropdown cells can resolve their
  // options. Maps the RETURN_DETAILS layout fields to the MTMetadataField shape
  // MTCommonTable's LazyEditCell expects (field_key + type + options).
  const detailMetadataFields = useMemo<MTMetadataField[]>(
    () =>
      detailLayoutFields.map(f => ({
        field_key: f.field_key,
        label: f.label,
        type: detailFieldType(f),
        required: !!f.required,
        placeholder: '',
        options: f.options,
        grid_column: 1,
        order: f.order,
        editable: f.editable,
        visible: f.visible,
        auto_fill: null,
        depends_on: null,
        validations: f.validations,
      })),
    [detailLayoutFields]
  )

  // Server-side pagination (India parity): the rows returned for the current
  // page ARE the page — each page change refetches, so no client-side slicing.
  const pagedDetailRows = detailRows

  const formHeading = `Returns Form - ${formViewData?.returnFormCode || currentForm}`

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      {/* AC1 - MTKeyFilterPanel renders Entity/TAN + the extra configured filters
          in its children slot. In Form view (viewMode) every filter + the action
          button are disabled and a Go Back affordance is shown. */}
      <MTKeyFilterPanel
        onViewClick={handleGenerate}
        actionLabel="Generate"
        viewMode={isFormView}
        onGoBack={() => guardUnsavedChanges(handleGoBack)}
        viewModeLabels={viewModeLabels}
        actionDisabled={!allExtraFilled}
      >
        {extraFilters.map(f => {
          const key = extraKey(f)
          const value =
            extraValues[key] ?? (isMultiSelectType(f.filterType) ? [] : '')
          const options = filterOptions[f.filterKey] ?? f.options ?? []
          // Sequential enabling: Return Form needs entity+TAN, Year needs Return Form, Month needs Year.
          const isYear = isYearFilter(f)
          const isMonth = isMonthFilter(f)
          const sequentialDisabled = isFormView
            ? true
            : isMonth
              ? !yearValue
              : isYear
                ? !returnFormValue
                : !entityAndTanSelected
          return (
            <CustomSelect
              key={key}
              value={value as string}
              onChange={(e: any) => {
                handleExtraChange(key, e.target.value)
                // Reset downstream filters when a parent changes.
                if (isReturnFormFilter(f)) {
                  if (yearFilter) handleExtraChange(extraKey(yearFilter), '')
                  if (monthFilter) handleExtraChange(extraKey(monthFilter), '')
                } else if (isYearFilter(f)) {
                  if (monthFilter) handleExtraChange(extraKey(monthFilter), '')
                }
              }}
              options={options}
              getOptionLabel={(o: any) => o.label}
              getOptionValue={(o: any) => o.value}
              placeholder={f.filterLabel}
              disabled={sequentialDisabled}
            />
          )
        })}
      </MTKeyFilterPanel>

      {isFormView ? (
        <Box>
          <Toolbar
            sx={{
              justifyContent: 'space-between',
              px: 3,
              py: 1,
              minHeight: '48px !important',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: '20px',
                color: 'var(--forbrand-secondary)',
                fontWeight: 500,
              }}
            >
              {formHeading}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title="Download">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Download return"
                    onClick={handleDownload}
                    disabled={downloadingRowId === viewRowId}
                    sx={{ color: 'var(--forbrand-primary)' }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </span>
              </Tooltip>
              {editMode ? (
                <>
                  <Tooltip title="Save">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Save return"
                        onClick={handleSaveEdit}
                        disabled={saving}
                        sx={{ color: 'var(--forbrand-primary)' }}
                      >
                        <SaveIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Cancel edit"
                        onClick={handleCancelEdit}
                        disabled={saving}
                        sx={{ color: 'var(--forbrand-primary)' }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              ) : (
                <Tooltip title="Edit">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Edit return"
                      onClick={handleEnterEdit}
                      sx={{ color: 'var(--forbrand-primary)' }}
                    >
                      <EditIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Toolbar>

          <Tabs
            value={formTab}
            onChange={(_e, v) =>
              guardUnsavedChanges(() => handleFormTabChange(v))
            }
            sx={{
              px: 3,
              borderBottom: '1px solid var(--border-subtel)',
              '& .MuiTabs-indicator': {
                backgroundColor: 'var(--forbrand-color)',
              },
              '& .Mui-selected': {
                color: 'var(--forbrand-primary) !important',
              },
            }}
          >
            {layoutTabs.length
              ? layoutTabs.map(t => (
                  <Tab
                    key={t.tab_key}
                    label={t.tab_label}
                    sx={{ textTransform: 'none' }}
                  />
                ))
              : [
                  <Tab
                    key="FORM"
                    label="Form"
                    sx={{ textTransform: 'none' }}
                  />,
                  <Tab
                    key="RETURN_DETAILS"
                    label="Return Details"
                    sx={{ textTransform: 'none' }}
                  />,
                ]}
          </Tabs>

          {(() => {
            const activeTab = layoutTabs[formTab]
            if (!activeTab) return null
            const isTableTab = activeTab.tab_key !== FORM_TAB_KEY
            if (isTableTab) {
              return (
                <Box sx={{ px: 1, py: 1 }}>
                  <MTCommonTable
                    columns={detailColumns as any}
                    data={pagedDetailRows}
                    loading={tabLoading || viewingRowId === viewRowId}
                    showFooter
                    emptyMessage="No return details found"
                    editMode={editMode}
                    editingData={detailEdits}
                    rawMetadataFields={detailMetadataFields}
                    onFieldChange={handleDetailFieldChange}
                    totalItems={detailTotalItems}
                    currentPage={detailPage}
                    itemsPerPage={detailPageSize}
                    onPageChange={page =>
                      guardUnsavedChanges(() =>
                        loadDetailPage(page, detailPageSize)
                      )
                    }
                    onItemsPerPageChange={size =>
                      guardUnsavedChanges(() => loadDetailPage(1, size))
                    }
                  />
                </Box>
              )
            }
            // FORM tab — render sections from metadata.
            // Show a layout-matching skeleton while this tab's data is being
            // fetched for the first time (not yet in the per-tab cache), so the
            // switch does not flash empty inputs.
            const tabSections = activeTab.sections
            const formTabLoading =
              (tabLoading || viewingRowId === viewRowId) && !formViewData
            if (formTabLoading) {
              return (
                <MTReturnFormSkeleton
                  sections={tabSections.length || 2}
                  fieldsPerSection={
                    tabSections[0]?.fields.filter(f => f.visible).length || 4
                  }
                />
              )
            }
            return (
              <Box sx={{ px: 3, py: 2.5 }}>
                {tabSections.map((section, si) => (
                  <Box
                    key={section.section_key}
                    sx={si > 0 ? { mt: 4 } : undefined}
                  >
                    <Typography sx={SECTION_TITLE_SX}>
                      {`${si + 1}. ${section.section_label}`}
                    </Typography>
                    <Box sx={FORM_GRID_SX}>
                      {section.fields
                        .filter(f => f.visible)
                        .slice()
                        .sort((a, b) => a.order - b.order)
                        .map(fld => (
                          <Box key={fld.field_key}>
                            <MTReturnFormField
                              label={fld.label}
                              fieldKey={fld.field_key}
                              value={
                                formEdits[fld.field_key] ??
                                resolveFieldValue(fld.field_key)
                              }
                              required={fld.required}
                              editMode={editMode && fld.editable}
                              onChange={handleFormFieldChange}
                            />
                            {formErrors[fld.field_key] && (
                              <Typography
                                sx={{
                                  fontSize: '12px',
                                  color: 'var(--forbrand-error, #d12121)',
                                  mt: 0.5,
                                }}
                              >
                                {formErrors[fld.field_key]}
                              </Typography>
                            )}
                          </Box>
                        ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            )
          })()}
        </Box>
      ) : (
        showTable && (
          <>
            <Toolbar
              sx={{
                justifyContent: 'space-between',
                px: 3,
                py: 1,
                minHeight: '48px !important',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: '20px',
                  color: 'var(--forbrand-secondary)',
                  fontWeight: 500,
                }}
              >
                Status of generated returns
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Tooltip title="Search">
                  <span>
                    <IconButton
                      size="small"
                      sx={{ color: 'var(--forbrand-primary)' }}
                      onClick={handleSearch}
                    >
                      <SearchIconBtn />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Toolbar>

            <Box>
              <MTCommonTable
                columns={displayColumns as any}
                data={data}
                loading={loading || generating}
                showFooter
                emptyMessage="No generated returns found"
                totalItems={totalItems}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </Box>
          </>
        )
      )}

      {React.createElement(Search as any, {
        open: searchOpen,
        onClose: handleCloseSearch,
        onApply: handleApplySearch,
        onReset: handleResetSearch,
        anchorEl,
        fields: searchFields,
        placement: 'bottom-start',
        setSearchCriteria,
        searchCriteria,
      })}

      {unsavedChangesDialog}
    </Box>
  )
}

/** Stable key for an extra (config-driven) filter value. */
function extraKey(f: MTReturnFilterConfig): string {
  return f.uuid || `${f.filterKey}_${f.displayOrder}`
}

export default MTReturnsTab
